import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useActionsCatalog } from "@/hooks/useActions";
import {
  ACTION_CODES,
  Action,
  ActionPayload,
} from "@/domain/actions.interface";
import actionValidationSchema, { ActionFormData } from "./validation.schema";

const normalizeSubActionIds = (
  subActions: Action["subActions"] | undefined
): string[] =>
  Array.isArray(subActions)
    ? subActions
        .map((sa) => (typeof sa === "string" ? sa : (sa?._id ?? "")))
        .filter(Boolean)
    : [];

type ActionModalProps = {
  open: boolean;
  onClose: () => void;
  action?: Action;
  onSave: (data: ActionPayload) => Promise<void>;
  isPending: boolean;
};

const DEFAULT_VALUES: ActionFormData = {
  name: "",
  code: ACTION_CODES[0],
  description: "",
  status: "ACTIVE",
  type: "",
  link: "",
  order: 0,
  icon: "",
  subActions: [],
};

const ActionModal: FC<ActionModalProps> = ({
  open,
  onClose,
  action,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!action;

  const { actionsCatalog, isLoadingActionsCatalog } = useActionsCatalog(open);

  const availableActions: Action[] = useMemo(() => {
    const list = actionsCatalog?.list || [];
    return action?._id ? list.filter((a) => a._id !== action._id) : list;
  }, [actionsCatalog, action?._id]);

  const codeOptions = useMemo(() => {
    const usedCodes = new Set(availableActions.map((a) => a.code));
    return ACTION_CODES.filter((code) => !usedCodes.has(code)).map((code) => ({
      key: code,
      value: code,
    }));
  }, [availableActions]);

  const methods = useForm<ActionFormData>({
    resolver: yupResolver(actionValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (action) {
      reset({
        name: action.name,
        code: action.code,
        description: action.description,
        status: action.status,
        type: action.type || "",
        link: action.link || "",
        order: action.order ?? 0,
        icon: action.icon || "",
        subActions: normalizeSubActionIds(action.subActions),
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [action, reset]);

  // In create mode the default code may already be registered; once the
  // catalog loads, snap the selection to the first still-available code.
  useEffect(() => {
    if (isEdit) return;
    const current = getValues("code");
    const isCurrentAvailable = codeOptions.some((opt) => opt.key === current);
    if (!isCurrentAvailable) {
      setValue("code", codeOptions[0]?.key ?? "", { shouldValidate: true });
    }
  }, [codeOptions, isEdit, getValues, setValue]);

  const onSubmit = async (data: ActionFormData) => {
    const currentUserId = userInfo?._id as string;
    const payload: ActionPayload = {
      name: data.name.trim(),
      code: data.code as ActionPayload["code"],
      description: data.description.trim(),
      status: data.status as ActionPayload["status"],
      order: data.order,
      ...(data.type ? { type: data.type as ActionPayload["type"] } : {}),
      ...(data.link ? { link: data.link.trim() } : {}),
      ...(data.icon ? { icon: data.icon.trim() } : {}),
      ...(data.subActions && data.subActions.length > 0
        ? { subActions: data.subActions as string[] }
        : {}),
      ...(isEdit ? { updatedBy: currentUserId } : { createdBy: currentUserId }),
    };
    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Action" : "New action"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 520,
        maxHeight: 720,
        maxWidth: 760,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="action-name"
                      label="Name"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={codeOptions}
                      value={field.value as string}
                      label="Code"
                      id="action-code"
                      required
                      error={!!errors.code}
                      errorMessage={errors.code?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="action-description"
                      label="Description"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.description}
                      helperText={
                        errors.description ? errors.description.message : null
                      }
                      multiline
                      minRows={3}
                      maxRows={6}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "MENU_OPTION", value: "Menu option" },
                        { key: "OPTION", value: "Option" },
                      ]}
                      value={field.value as string}
                      label="Type"
                      id="action-type"
                      showClearOption
                      error={!!errors.type}
                      errorMessage={errors.type?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "ACTIVE", value: "Active" },
                        { key: "INACTIVE", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Status"
                      id="action-status"
                      required
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="link"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="action-link"
                      label="Link"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.link}
                      helperText={errors.link ? errors.link.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Controller
                  name="order"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="action-order"
                      label="Order"
                      type="number"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? 0 : Number(e.target.value)
                        )
                      }
                      error={!!errors.order}
                      helperText={errors.order ? errors.order.message : null}
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Controller
                  name="icon"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="action-icon"
                      label="Icon"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.icon}
                      helperText={errors.icon ? errors.icon.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="subActions"
                  control={control}
                  render={({ field }) => {
                    const selectedIds = (field.value as string[]) || [];
                    const selectedObjects = availableActions.filter((a) =>
                      selectedIds.includes(a._id)
                    );
                    return (
                      <Autocomplete
                        multiple
                        options={availableActions}
                        loading={isLoadingActionsCatalog}
                        disabled={isPending}
                        getOptionLabel={(option) =>
                          option.name || option._id || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option._id === value._id
                        }
                        value={selectedObjects}
                        onChange={(_e, selected) => {
                          field.onChange(selected.map((a) => a._id));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Sub actions"
                            placeholder="Select sub actions"
                            error={!!errors.subActions}
                            helperText={
                              (errors.subActions as { message?: string })
                                ?.message || ""
                            }
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default ActionModal;
